import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { projectsApi } from '../../api/projects';
import { downloadProjectBackup } from '../../utils/downloadProjectBackup';
import { storiesApi } from '../../api/stories';
import EditableName from '../Common/EditableName';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';
import CreateProjectModal from '../Sidebar/BrowseTab/CreateProjectModal';
import ImportUploadModal from '../DocumentImport/ImportUploadModal';
import { applyImportNext, landCreatedBook } from '../DocumentImport/importNext';
import ProjectSettingsPanel from '../Settings/ProjectSettingsPanel';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';
import AtlasIcon from '../icons/AtlasIcon';
import RailTooltip from '../Common/RailTooltip';
import NewSessionDialog from '../Session/NewSessionDialog';
import { isProjectOpen, navDisabledWhy, navWhyLine } from '../../utils/navDisabledWhy';
import { journeyAccessibleName, PROJECT_OPEN_FAILED, TRY_AGAIN } from '../../utils/writerCopy';
import {
  BOOK_NAV_HOVER_TIPS,
  BOOK_TOOL_IDS,
  SIDEBAR_MAP_GROUPS,
  SIDEBAR_SIDE_DOOR,
  SERIES_SIDEBAR_MAP_GROUPS,
  flattenMapItems,
  flattenSeriesMapItems,
  isBookNotesRouteActive,
  isChatSessionsPath,
  isDiscoveryPath,
  isProjectNotesPath,
  createSessionLaunch,
  isSeriesAltitude,
  isSeriesRoomPath,
  isSeriesSnapshotPath,
} from './sidebarMap';
import { isNotesPath, notesPathForAltitude } from '../../utils/seriesNotes';
import { seriesTimelinePath } from '../../utils/seriesLanding';
import { seriesCardsPath } from '../../utils/seriesSnapshots';
import { plottrackPathForBook } from '../../utils/plottrackPath';
import { styleCardsPathForBook } from '../../utils/styleCardsPath';
import { uploadBookId } from '../../utils/uploadBookId';
import { heldMenuIdentity } from '../../utils/menuIdentity';
import { pathRoomPathForBook } from '../../utils/pathRoomPath';
import { bookDiscoverySessionId, openBookDiscovery } from '../../utils/bookDiscoverySession';
import { runAfterLeaveGuard } from '../../utils/draftLeaveGuard';
import './LeftNav.css';

/**
 * CSS pixels of menu height that fit in the visible sidebar.
 * getBoundingClientRect is after sidebar zoom, and max-height is before it,
 * so the visual gap is divided by zoom. A 160px floor used to paint past
 * the pane, and the sidebar clip hid Settings and Upload a doc.
 */
export function bookMenuMaxHeight({
  menuTop,
  paneTop,
  paneBottom,
  viewportBottom,
  zoom = 1,
}) {
  const scale = Number(zoom) > 0 ? Number(zoom) : 1;
  const top = Math.max(Number(menuTop) || 0, Number(paneTop) || 0);
  const bottom = Math.min(
    Number.isFinite(paneBottom) ? paneBottom : viewportBottom,
    Number.isFinite(viewportBottom) ? viewportBottom : paneBottom,
  );
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) return 0;
  return Math.max(0, Math.floor((bottom - top - 8) / scale));
}

/**
 * LeftNav — project map (#653). Not a chat list. Not equal tabs.
 *
 * Book sidebar: book-name menu plus Idea, Outline, Write, Notes,
 * Style Notes, and Docs.
 * Older rooms stay in the book menu until their own slices. No stage gates.
 *
 * Current room marked. No padlocks. No “finish chat first.”
 *
 * Series (#655 / #670) is an altitude above books, not a sibling of this map.
 * Single-book projects have no Series button. On a series, the control
 * is labeled Series (room) / Back to Series (inside a book) — never
 * the project name alone, which an Untitled series is unrecognizable as.
 * At series altitude the map is Talk first (Series Discovery, with Chat
 * Sessions and Create New Session quiet under it), then Timeline (the existing corkboard),
 * then Snapshots (one Series Notes room). Started books (real
 * story records, not Timeline idea pins) are listed on this panel so
 * a writer can jump into a book without leaving. Tasks is the first
 * book-map group. No Outline. No Story Board. No second Timeline
 * page. No book-level Timeline. Docs is one pile, two doors (#823): Book
 * Planning Docs on the book map; Series Docs on the Timeline
 * toggle. Docs is not a mode-switch. Upload stays a side door.
 */

const ALL_ITEMS = flattenMapItems();
const SERIES_RAIL_ITEMS = flattenSeriesMapItems();
const BOOK_RAIL_ITEMS = [...ALL_ITEMS, SIDEBAR_SIDE_DOOR];

/**
 * "No project" is the empty list, after a load that succeeded.
 * While sign-in or the list is still in progress, this stays a loading
 * line. A list that came back with projects does not say there are none.
 */
function ProjectIdentityPending({
  projectOpenError,
  projectsLoaded,
  projects,
  projectsError,
  onRetry,
}) {
  if (projectOpenError) {
    return (
      <>
        <span className="left-nav__identity-name">{PROJECT_OPEN_FAILED}</span>
        <button
          type="button"
          className="left-nav__identity-retry"
          onClick={onRetry}
        >
          {TRY_AGAIN}
        </button>
      </>
    );
  }
  const listReady = projectsLoaded !== false;
  const none = listReady && !projectsError && Array.isArray(projects) && projects.length === 0;
  if (!listReady) {
    return (
      <span className="left-nav__identity-name" role="status">Loading...</span>
    );
  }
  if (!none) return null;
  return <span className="left-nav__identity-name">No project</span>;
}

function LeftNav({
  onToolClick,
  isCapturePanelOpen,
  isLearnPanelOpen,
  isBookmarksPanelOpen,
  hasStory,
  onSessionSelect,
  collapsed = false,
  onToggleCollapse,
}) {
  const routerNavigate = useNavigate();
  const navigate = (...args) => {
    runAfterLeaveGuard(() => routerNavigate(...args));
  };
  const location = useLocation();
  const {
    stories,
    projects,
    projectsError,
    currentProject,
    currentStory,
    currentSession,
    currentSessionId,
    setCurrentProject,
    setCurrentStory,
    clearCurrentSession,
    refreshProjects,
    projectOpenError,
    retryProjectRestore,
    leaveProject,
    projectsLoaded,
    isAdmin,
  } = useApp();
  const { switchStory: navigateToStory } = useStoryNavigation();
  const heldProjectRef = useRef(null);
  const heldStoryRef = useRef(null);
  const onProjectsHome = location.pathname === '/projects';
  const menuIdentity = heldMenuIdentity({
    onProjectsHome,
    currentProject,
    currentStory,
    heldProject: heldProjectRef.current,
    heldStory: heldStoryRef.current,
  });
  heldProjectRef.current = menuIdentity.nextHeldProject;
  heldStoryRef.current = menuIdentity.nextHeldStory;
  const displayProject = menuIdentity.project;
  const displayStory = menuIdentity.story;

  const { confirm, modalProps } = useConfirm();
  const [showBookJump, setShowBookJump] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [createSessionCtx, setCreateSessionCtx] = useState(null);
  // #1135: Book tools starts open so Style Notes, Story Rules, Framework
  // Builder, and Docs are visible. Nested Book Notes categories are
  // children of Book Notes and show with that item — no second click.
  const [bookToolsOpen, setBookToolsOpen] = useState(true);
  const [bookMenuOpen, setBookMenuOpen] = useState(false);
  const bookMenuRef = useRef(null);
  const [renameTick, setRenameTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportChoicesOpen, setExportChoicesOpen] = useState(false);
  const exportItemRef = useRef(null);
  const exportMenuId = useId();
  const textLabelId = useId();
  const textHintId = useId();
  const backupLabelId = useId();
  const backupHintId = useId();

  useEffect(() => {
    setBookMenuOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (!bookMenuOpen) return undefined;
    const fit = () => {
      const menu = bookMenuRef.current;
      if (!menu) return;
      const pane = menu.closest('.brainstormy-sidebar__content')
        || menu.closest('.brainstormy-sidebar')
        || menu.closest('.left-nav');
      const scaler = menu.closest('.brainstormy-sidebar__content-scaler');
      const zoom = Number(getComputedStyle(scaler || menu).zoom) || 1;
      const menuRect = menu.getBoundingClientRect();
      const paneRect = pane ? pane.getBoundingClientRect() : null;
      const room = bookMenuMaxHeight({
        menuTop: menuRect.top,
        paneTop: paneRect ? paneRect.top : 0,
        paneBottom: paneRect ? paneRect.bottom : window.innerHeight,
        viewportBottom: window.innerHeight,
        zoom,
      });
      menu.style.maxHeight = `${room}px`;
    };
    fit();
    const pane = bookMenuRef.current?.closest('.brainstormy-sidebar__content');
    window.addEventListener('resize', fit);
    pane?.addEventListener('scroll', fit);
    return () => {
      window.removeEventListener('resize', fit);
      pane?.removeEventListener('scroll', fit);
    };
  }, [bookMenuOpen]);

  useEffect(() => {
    if (!bookMenuOpen) return undefined;
    const onPointer = (event) => {
      if (!event.target.closest?.('.left-nav__book')) setBookMenuOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (exportChoicesOpen) {
        setExportChoicesOpen(false);
        exportItemRef.current?.focus();
        return;
      }
      setBookMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [bookMenuOpen, exportChoicesOpen]);

  useEffect(() => {
    if (!bookMenuOpen) setExportChoicesOpen(false);
  }, [bookMenuOpen]);

  const downloadTextFile = () => {
    if (!currentProject?.id || exporting) return;
    setExporting(true);
    setExportError('');
    projectsApi.export(currentProject.id)
      .then(async (response) => {
        const blob = await response.blob();
        const disposition = response.headers?.get?.('Content-Disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/);
        const fallbackSlug = (currentProject.name || 'project')
          .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'project';
        const filename = match
          ? match[1]
          : `${fallbackSlug}-export-${new Date().toISOString().slice(0, 10)}.md`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        setExportError('');
        setBookMenuOpen(false);
      })
      .catch((err) => {
        setExportError(err?.message || 'Failed to export project');
      })
      .finally(() => setExporting(false));
  };

  const downloadBackupFile = () => {
    if (!currentProject?.id || backingUp) return;
    setBackingUp(true);
    setExportError('');
    downloadProjectBackup(currentProject)
      .then(() => {
        setExportError('');
        setBookMenuOpen(false);
      })
      .catch((err) => {
        setExportError(err?.message || 'This backup could not be prepared. Try again.');
      })
      .finally(() => setBackingUp(false));
  };

  const resolvedDiscoveryId = bookDiscoverySessionId({
    currentStory: displayStory,
    currentProject: displayProject,
    stories,
  });
  const discoveryIds = [
    resolvedDiscoveryId,
    currentStory?.discovery_session_id,
    currentProject?.discovery_session_id,
  ].filter(Boolean);
  const activeSessionId = currentSessionId || currentSession?.id || null;
  const isSeries = Boolean(displayProject?.is_series);
  const onProjectNotes = isProjectNotesPath(location.pathname);
  const onNotesRoom = isNotesPath(location.pathname);
  const onSeriesSnapshots = isSeriesSnapshotPath(location.pathname);
  const inSeriesRoom = isSeries && isSeriesRoomPath(location.pathname);
  const inSeriesLayer = isSeries && (
    inSeriesRoom || onSeriesSnapshots || (onProjectNotes && !displayStory)
  );
  const atSeriesAltitude = isSeriesAltitude({
    isSeries,
    pathname: location.pathname,
    currentStory: displayStory,
    currentSession,
  });
  // Create New Session stays in the open book. Series altitude with no
  // book does not launch (#1226).
  const sessionLaunch = createSessionLaunch({
    atSeriesAltitude,
    currentProject: displayProject,
    currentStory: displayStory,
    currentSession,
  });
  const canCreateSession = Boolean(sessionLaunch?.storyId);
  const showBookMap = !atSeriesAltitude && !onProjectsHome;
  const mapGroups = atSeriesAltitude ? SERIES_SIDEBAR_MAP_GROUPS : SIDEBAR_MAP_GROUPS;
  const railItems = atSeriesAltitude ? SERIES_RAIL_ITEMS : BOOK_RAIL_ITEMS;
  const projectOpen = isProjectOpen({
    currentProject: displayProject,
    projects,
    pathname: location.pathname,
  });
  const navWhyArgs = {
    hasStory,
    currentProject: displayProject,
    currentStory: displayStory,
    canCreateSession,
    projects,
    pathname: location.pathname,
  };

  const goToProjects = () => {
    runAfterLeaveGuard(() => {
      leaveProject();
      clearCurrentSession?.();
      routerNavigate('/projects');
      onSessionSelect?.();
    });
  };

  const handleDeleteProject = async () => {
    if (!currentProject) return;
    setShowProjectSettings(false);
    const ok = await confirm({
      title: 'Delete project?',
      message: `Delete project "${currentProject.name}" and all its stories and sessions? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await projectsApi.delete(currentProject.id);
      leaveProject();
      clearCurrentSession?.();
      await refreshProjects();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  const isPanel = (item) => item.kind === 'panel';

  const isPanelOpen = (id) =>
    (id === 'captures' && isCapturePanelOpen) ||
    (id === 'learn' && isLearnPanelOpen) ||
    (id === 'bookmarks' && isBookmarksPanelOpen);

  const isItemActive = (item) => {
    if (item.kind === 'panel') return isPanelOpen(item.id);
    if (item.id === 'discovery') {
      return isDiscoveryPath(location.pathname, activeSessionId, discoveryIds);
    }
    if (item.id === 'sessions') {
      return isChatSessionsPath(location.pathname, activeSessionId, discoveryIds);
    }
    if (item.id === 'framework-builder' || item.path === '/frameworks') {
      return location.pathname.startsWith('/frameworks');
    }
    if (item.id === 'plot-notebook' || item.path === '/dossiers') {
      return location.pathname === '/dossiers' || location.pathname.startsWith('/dossiers/');
    }
    if (item.id === 'notes') return onNotesRoom;
    if (item.kind === 'timeline' || item.id === 'timeline') {
      return isSeriesRoomPath(location.pathname);
    }
    if (item.id === 'series-cards') {
      return location.pathname.includes('/series-cards')
        || location.pathname.includes('/series-offers')
        || location.pathname.includes('/series-dossiers');
    }
    if (item.id === 'cards') {
      return (location.pathname || '').startsWith('/bibles');
    }
    if (item.bookNotesCategory) {
      return isBookNotesRouteActive(item, location.pathname, location.search);
    }
    if (item.kind === 'route') {
      if (item.path === '/bibles') {
        return location.pathname.startsWith('/bibles');
      }
      if (item.id === 'path' || item.path === '/tasks' || item.path === '/path') {
        return location.pathname === '/tasks'
          || location.pathname.startsWith('/tasks/')
          || location.pathname === '/path'
          || location.pathname.startsWith('/path/');
      }
      return location.pathname.startsWith(item.path);
    }
    return false;
  };

  const bookToolActive = showBookMap && railItems.some((item) => (
    BOOK_TOOL_IDS.has(item.id) && isItemActive(item)
  ));
  useEffect(() => {
    if (bookToolActive) setBookToolsOpen(true);
  }, [bookToolActive]);

  const openDiscovery = async () => {
    if (atSeriesAltitude && currentProject?.id) {
      runAfterLeaveGuard(() => {
        setCurrentStory(null);
        if (currentProject.discovery_session_id) {
          routerNavigate(`/chat/${currentProject.discovery_session_id}`);
          return;
        }
        routerNavigate(`/projects/${currentProject.id}/discovery`);
      });
      return;
    }
    try {
      await openBookDiscovery({
        currentStory,
        currentProject,
        stories,
        navigate,
        setCurrentStory,
        ensureDiscoverySession: storiesApi.ensureDiscoverySession,
      });
    } catch (error) {
      console.error('Failed to open Discovery:', error);
    }
  };

  const openCreateSession = () => {
    if (!sessionLaunch?.storyId) return;
    // Snapshot ids so a later session hydrate cannot unmount the picker.
    setCreateSessionCtx(sessionLaunch);
    setShowNewSession(true);
  };

  const selectItem = (item) => {
    if (navDisabledWhy(item, navWhyArgs)) return;
    if (item.needsStory && !hasStory) return;
    if (item.needsProject && !displayProject) return;
    if (item.kind === 'create' && !canCreateSession) return;
    if (atSeriesAltitude && item.id === 'sessions') {
      setCurrentStory(null);
    }
    switch (item.kind) {
      case 'route':
        if (item.id === 'plottrack') {
          // Book-map door: the book you are in is the URL (#849).
          // Do not send bare /plottrack and hope first paint recovers.
          onToolClick({
            id: item.id,
            path: plottrackPathForBook({
              pathname: location.pathname,
              currentStory: displayStory || currentStory,
              currentSession,
            }),
          });
          break;
        }
        if (item.id === 'path') {
          onToolClick({
            id: item.id,
            path: pathRoomPathForBook({
              pathname: location.pathname,
              currentStory: displayStory || currentStory,
              currentSession,
            }),
          });
          break;
        }
        if (item.id === 'style-cards') {
          const path = styleCardsPathForBook({
            pathname: location.pathname,
            currentStory: displayStory || currentStory,
            currentSession,
          });
          if (path) onToolClick({ id: item.id, path });
          break;
        }
        onToolClick({ id: item.id, path: item.path });
        break;
      case 'panel':
        onToolClick({ id: item.id, panel: true });
        break;
      case 'action':
        if (item.id === 'upload') setShowUpload(true);
        break;
      case 'discovery':
        openDiscovery();
        break;
      case 'notes':
        runAfterLeaveGuard(() => {
          if (currentProject?.id) {
            routerNavigate(notesPathForAltitude({
              projectId: currentProject.id,
              storyId: currentStory?.id,
            }));
          }
          onSessionSelect?.();
        });
        return;
      case 'timeline':
        runAfterLeaveGuard(() => {
          if (currentProject?.id) {
            setCurrentStory(null);
            routerNavigate(seriesTimelinePath(currentProject.id));
          }
          onSessionSelect?.();
        });
        return;
      case 'series-cards':
        runAfterLeaveGuard(() => {
          if (currentProject?.id) {
            setCurrentStory(null);
            routerNavigate(seriesCardsPath(currentProject.id));
          }
          onSessionSelect?.();
        });
        return;
      case 'create':
        openCreateSession();
        break;
      default:
        break;
    }
    onSessionSelect?.();
  };

  const openSeriesRoom = () => {
    if (!projectOpen || !currentProject?.id) return;
    runAfterLeaveGuard(() => {
      setShowBookJump(false);
      setCurrentStory(null);
      routerNavigate(`/projects/${currentProject.id}/series`);
      onSessionSelect?.();
    });
  };

  const jumpToBook = (story) => {
    runAfterLeaveGuard(() => {
      navigateToStory(currentProject, story);
      setShowBookJump(false);
      onSessionSelect?.();
    });
  };

  const handleProjectCreated = async (newProject, options) => {
    await refreshProjects();
    setShowCreateProject(false);
    setCurrentProject(newProject);
    if (newProject?.story_id) {
      setCurrentStory({
        id: newProject.story_id,
        project_id: newProject.id,
        name: newProject.name,
        discovery_session_id: newProject.discovery_session_id || null,
      });
    }
    if (landCreatedBook(navigate, newProject, options)) return;
    applyImportNext(navigate, options, newProject, {
      currentStoryId: newProject?.story_id || currentStory?.id,
    });
  };

  const handleSessionCreated = (session) => {
    const id = session?.id;
    if (id) navigate(`/chat/${id}`);
  };

  const renderItem = (item, railed) => {
    const why = navDisabledWhy(item, navWhyArgs);
    const whyLine = navWhyLine(item, navWhyArgs);
    const disabled = Boolean(why);
    const active = isItemActive(item);
    const panel = isPanel(item);
    const cls = [
      railed ? 'left-nav__rail-item' : 'left-nav__item',
      active ? (panel ? 'is-panel-open' : 'is-active') : '',
      disabled ? 'is-disabled' : '',
      item.quiet && !railed ? 'is-quiet' : '',
      item.prominent && !railed && !disabled ? 'is-create' : '',
    ].filter(Boolean).join(' ');
    const accessible = journeyAccessibleName(item.plain, item.label);
    const label = railed ? accessible : undefined;
    // Uncollapsed book items: Joel's hover tip (#957 / #963). Disabled
    // why still wins when a project is open (#915). No project: muted
    // title only, no helper line and no why tooltip (#971). Collapsed
    // rail keeps the label tooltip, not these tips. Series map items
    // have no hoverTip.
    const title = whyLine || (!railed && !disabled ? item.hoverTip : undefined);
    return (
      <span className="left-nav__item-wrap">
        <button
          type="button"
          className={cls}
          aria-current={active && !panel ? 'page' : undefined}
          aria-pressed={panel ? active : undefined}
          disabled={disabled}
          aria-label={accessible}
          title={title}
          onClick={() => selectItem(item)}
        >
          {item.step && !railed ? (
            <span className="left-nav__step" aria-hidden="true">{item.step}</span>
          ) : (
            <span className="left-nav__item-icon">
              <AtlasIcon name={item.icon} size={railed ? 20 : 18} />
              {panel && active && <span className="left-nav__dot" aria-hidden="true" />}
            </span>
          )}
          {!railed && item.plain ? (
            <span className="left-nav__item-copy">
              <span className="left-nav__item-primary">{item.plain}</span>
              <span className="left-nav__item-secondary">{item.label}</span>
            </span>
          ) : !railed ? (
            <span className="left-nav__item-label">{item.label}</span>
          ) : null}
        </button>
        {!railed && whyLine && (
          <span className="left-nav__item-why">{whyLine}</span>
        )}
      </span>
    );
  };

  const mapAndModals = (body) => (
    <>
      {body}
      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          onCreated={handleProjectCreated}
        />
      )}
      {showUpload && currentProject && (
        <ImportUploadModal
          project={currentProject}
          storyId={uploadBookId({
            pathname: location.pathname,
            currentStory: displayStory || currentStory,
            currentSession,
            atSeriesAltitude,
            onProjectNotes,
          })}
          showPromptChooser
          onClose={(payload) => {
            setShowUpload(false);
            applyImportNext(navigate, payload, currentProject, {
              currentStoryId: currentStory?.id,
            });
          }}
        />
      )}
      {showNewSession && createSessionCtx?.storyId && (
        <NewSessionDialog
          storyId={createSessionCtx.storyId}
          projectId={createSessionCtx.projectId}
          storyName={
            (currentStory?.id === createSessionCtx.storyId && currentStory?.name)
              || (stories || []).find((s) => s.id === createSessionCtx.storyId)?.name
              || ''
          }
          seriesAltitude={false}
          onClose={() => {
            setShowNewSession(false);
            setCreateSessionCtx(null);
          }}
          onSessionCreated={handleSessionCreated}
        />
      )}
      {currentProject && (
        <ProjectSettingsPanel
          project={currentProject}
          storyCount={stories?.length || 0}
          isOpen={showProjectSettings}
          onClose={() => setShowProjectSettings(false)}
          onDeleteProject={handleDeleteProject}
        />
      )}
      <ConfirmModal {...modalProps} />
    </>
  );

  if (collapsed) {
    return mapAndModals(
      <nav className="left-nav left-nav--rail" aria-label="Project map">
        {onToggleCollapse && (
          <RailTooltip label="Show left panel">
            <button
              type="button"
              className="left-nav__rail-toggle"
              onClick={onToggleCollapse}
              aria-label="Show left panel"
              title="Show left panel"
            >
              <span aria-hidden="true">«</span>
            </button>
          </RailTooltip>
        )}
        <RailTooltip label="All stories">
          <button
            type="button"
            className={`left-nav__rail-item ${location.pathname === '/projects' ? 'is-active' : ''}`}
            onClick={goToProjects}
            aria-label="All stories"
          >
            <span className="left-nav__item-icon"><AtlasIcon name="projects" size={20} /></span>
          </button>
        </RailTooltip>
        {!onProjectsHome && <span className="left-nav__rail-divider" aria-hidden="true" />}
        {!onProjectsHome && isSeries && (
          <RailTooltip label="Series">
            <button
              type="button"
              className={`left-nav__rail-item ${inSeriesLayer ? 'is-active' : ''} ${!projectOpen ? 'is-disabled' : ''}`}
              onClick={openSeriesRoom}
              aria-label="Series"
              aria-current={inSeriesLayer ? 'page' : undefined}
              disabled={!projectOpen}
            >
              <span className="left-nav__item-icon"><AtlasIcon name="series" size={20} /></span>
            </button>
          </RailTooltip>
        )}
        {!onProjectsHome && railItems.filter((item) => !item.omitFromRail).map((item) => (
          <RailTooltip key={item.id} label={journeyAccessibleName(item.plain, item.label)}>
            {renderItem(item, true)}
          </RailTooltip>
        ))}
      </nav>,
    );
  }

  return mapAndModals(
    <nav className="left-nav" aria-label="Project map">
      <div className="left-nav__book">
        {onProjectsHome ? (
          <p className="left-nav__book-home">All stories</p>
        ) : displayProject ? (
          <button
            type="button"
            className="left-nav__book-btn"
            aria-expanded={bookMenuOpen}
            aria-haspopup="menu"
            onClick={() => setBookMenuOpen((open) => !open)}
          >
            {atSeriesAltitude
              ? (displayProject.name || 'This series')
              : (displayStory?.name || displayProject.name || 'This book')}
            <span aria-hidden="true"> ▾</span>
          </button>
        ) : (
          <>
            <ProjectIdentityPending
              projectOpenError={projectOpenError}
              projectsLoaded={projectsLoaded}
              projects={projects}
              projectsError={projectsError}
              onRetry={retryProjectRestore}
            />
            <button type="button" className="left-nav__book-item" onClick={goToProjects}>
              All stories
            </button>
          </>
        )}
        {bookMenuOpen && displayProject && !onProjectsHome && (
          <div className="left-nav__book-menu" role="menu" aria-label="Book menu" ref={bookMenuRef}>
            <div className="left-nav__book-rename">
              <EditableName
                className="left-nav__identity-name"
                showEditIcon={false}
                editLabel="Rename book"
                forceEdit={renameTick}
                value={atSeriesAltitude ? displayProject.name : (displayStory?.name || displayProject.name)}
                onSave={async (name) => {
                  const story = currentStory || displayStory;
                  const project = currentProject || displayProject;
                  if (atSeriesAltitude || !story?.id) {
                    await projectsApi.update(project.id, { name });
                    setCurrentProject({ ...project, name });
                  } else {
                    await storiesApi.update(story.id, { name });
                    setCurrentStory({
                      ...story,
                      name,
                      project_id: story.project_id || project?.id,
                    });
                    // The projects card and its delete dialog read the
                    // project title. A sole novel keeps that title with
                    // the book. project_id stays on the story so this
                    // project update does not drop the open book.
                    const bookCount = (stories || []).filter((item) => item?.id).length;
                    if (project && !project.is_series && bookCount === 1) {
                      setCurrentProject({ ...project, name });
                    }
                    await refreshProjects();
                  }
                }}
              />
            </div>
            <button
              type="button"
              role="menuitem"
              className="left-nav__book-item"
              onClick={() => setRenameTick((tick) => tick + 1)}
            >
              Rename book
            </button>
            <button
              type="button"
              role="menuitem"
              className="left-nav__book-item"
              aria-label="All stories"
              onClick={goToProjects}
            >
              ← All stories
            </button>
            <button
              type="button"
              role="menuitem"
              className="left-nav__book-item"
              ref={exportItemRef}
              aria-expanded={exportChoicesOpen}
              aria-haspopup="menu"
              aria-controls={exportChoicesOpen ? exportMenuId : undefined}
              onClick={() => setExportChoicesOpen((open) => !open)}
            >
              Export
            </button>
            {exportChoicesOpen && (
              <div
                id={exportMenuId}
                className="left-nav__book-submenu"
                role="menu"
                aria-label="Export"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="left-nav__book-item left-nav__book-choice"
                  aria-labelledby={textLabelId}
                  aria-describedby={textHintId}
                  disabled={exporting}
                  onClick={downloadTextFile}
                >
                  <span id={textLabelId} className="left-nav__book-choice-label">Text file</span>
                  <span id={textHintId} className="left-nav__book-hint">
                    Your whole project in one text file, to read or use in other writing apps.
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="left-nav__book-item left-nav__book-choice"
                  aria-labelledby={backupLabelId}
                  aria-describedby={backupHintId}
                  disabled={backingUp}
                  onClick={downloadBackupFile}
                >
                  <span id={backupLabelId} className="left-nav__book-choice-label">Backup</span>
                  <span id={backupHintId} className="left-nav__book-hint">
                    A full copy, chats included, that Brainstormy can restore later. Keep it somewhere safe.
                  </span>
                </button>
              </div>
            )}
            {exportError && (
              <p className="left-nav__book-error" role="alert">{exportError}</p>
            )}
            <button
              type="button"
              role="menuitem"
              className="left-nav__book-item"
              onClick={() => {
                navigate('/search');
                setBookMenuOpen(false);
              }}
            >
              Search
            </button>
            <button
              type="button"
              role="menuitem"
              className="left-nav__book-item"
              onClick={() => {
                onToolClick?.({ id: 'learn', panel: true });
                setBookMenuOpen(false);
              }}
            >
              Research
            </button>
            {currentStory?.id && (
              <button
                type="button"
                role="menuitem"
                className="left-nav__book-item"
                onClick={() => {
                  navigate('/graph');
                  setBookMenuOpen(false);
                }}
              >
                Story Dashboard
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                role="menuitem"
                className="left-nav__book-item"
                onClick={() => {
                  onToolClick?.({ id: 'debug', panel: true });
                  setBookMenuOpen(false);
                }}
              >
                Debug
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="left-nav__book-item"
              onClick={() => {
                setShowProjectSettings(true);
                setBookMenuOpen(false);
              }}
            >
              Settings
            </button>
            {!atSeriesAltitude && (
              <button
                type="button"
                role="menuitem"
                className="left-nav__book-item"
                title={BOOK_NAV_HOVER_TIPS.upload}
                onClick={() => {
                  setShowUpload(true);
                  setBookMenuOpen(false);
                }}
              >
                Upload a doc
              </button>
            )}
            {isSeries && (
              <button
                type="button"
                role="menuitem"
                className="left-nav__book-item"
                onClick={() => {
                  openSeriesRoom();
                  setBookMenuOpen(false);
                }}
                disabled={!projectOpen}
                aria-current={inSeriesLayer ? 'page' : undefined}
              >
                {inSeriesLayer ? 'Series' : 'Back to Series'}
              </button>
            )}
            {isSeries && !atSeriesAltitude && currentStory?.id && currentProject?.id && (
              <button
                type="button"
                role="menuitem"
                className="left-nav__book-item"
                onClick={() => {
                  navigate(seriesCardsPath(currentProject.id));
                  setBookMenuOpen(false);
                }}
              >
                Series Notes
              </button>
            )}
            {isSeries && !atSeriesAltitude && (
              <button
                type="button"
                role="menuitem"
                className="left-nav__book-item"
                aria-expanded={showBookJump}
                aria-label="Choose a book"
                onClick={() => setShowBookJump((v) => !v)}
              >
                Choose a book
              </button>
            )}
          </div>
        )}
      </div>

      {(atSeriesAltitude || (showBookJump && isSeries && !atSeriesAltitude)) && (
        <div className="left-nav__switcher" role="listbox" aria-label="Series books">
          <p className="left-nav__switcher-label">Books in this series</p>
          <ul>
            {(stories || []).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`left-nav__switch ${currentStory?.id === s.id ? 'is-current' : ''}`}
                  aria-current={currentStory?.id === s.id ? 'true' : undefined}
                  onClick={() => jumpToBook(s)}
                >
                  {s.name}
                </button>
              </li>
            ))}
            {(!stories || stories.length === 0) && (
              <li className="left-nav__switcher-empty">No books started yet</li>
            )}
          </ul>
        </div>
      )}

      {(showBookMap || atSeriesAltitude) && (
        <div className="left-nav__map">
          {mapGroups.map((group) => {
            const folded = Boolean(group.fold);
            const pinned = new Set(group.pinnedIds || []);
            const visibleItems = folded
              ? group.items.filter((item) => pinned.has(item.id) || bookToolsOpen)
              : group.items;
            return (
              <div key={group.id} className="left-nav__group">
                {folded ? (
                  <button
                    type="button"
                    className="left-nav__fold"
                    aria-expanded={bookToolsOpen}
                    onClick={() => setBookToolsOpen((open) => !open)}
                  >
                    {group.label}
                  </button>
                ) : (
                  <p className="left-nav__group-label">{group.label}</p>
                )}
                {group.hint && (
                  <p className="left-nav__group-hint">{group.hint}</p>
                )}
                <ul className="left-nav__items">
                  {visibleItems.map((item) => (
                    <li key={item.id}>
                      {renderItem(item, false)}
                      {item.children && (
                        <ul className="left-nav__quiet">
                          {item.children.map((child) => (
                            <li key={child.id}>{renderItem(child, false)}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {atSeriesAltitude && (
        <div className="left-nav__side-door">
          {renderItem(SIDEBAR_SIDE_DOOR, false)}
        </div>
      )}
    </nav>,
  );
}

export default LeftNav;
